<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useTheme } from 'vuetify'

import CommonLayer from '@kidzonet/vue3-common-layer/src/CommonLayer/index.vue'
import { MainMenuItemInterface } from '@kidzonet/vue3-common-layer/src/types'

interface RedocApiContainerProps {
    back?: string | { name: string }
    backButton?: string
    oneLine: boolean
    menu: MainMenuItemInterface[]
}

withDefaults(defineProps<RedocApiContainerProps>(), {
    back: '/',
    backButton: '/',
})

const theme = useTheme()

const backButtonTop = ref(72)

const backButtonStyle = computed(() => ({
    top: `${backButtonTop.value}px`,
}))

window.addEventListener('scroll', () => {
    const position = window.scrollY

    const topPoint = 50
    const bottomPoint = 70

    if (position > topPoint && backButtonTop.value > 15) {
        backButtonTop.value = Math.max(15, backButtonTop.value - position + topPoint)
    } else if (position < bottomPoint) {
        backButtonTop.value = Math.min(72, backButtonTop.value - position + bottomPoint)
    }
})
</script>

<template>
    <CommonLayer
        :menu="menu"
        :root="back"
        :one-line="oneLine"
        class="redoc-api"
    >
        <template #before-logo-link>
            <div class="redoc-api-logo-back-button">
                <router-link
                    :style="backButtonStyle"
                    class="redoc-api-logo-back-button__button"
                    :to="backButton"
                >
                    ‹
                </router-link>
            </div>
        </template>
        <template #after-logo>
            <div class="redoc-api__logo">
                API
            </div>
        </template>
        <template #content>
            <div class="kidzonet-back-button-container">
                <router-link
                    :class="{
                        'kidzonet-back-button': true,
                        'kidzonet-back-button--dark': theme.global.current.value.dark,
                    }"
                    :to="backButton"
                >
                    ‹  Back
                </router-link>
            </div>
            <router-view />
        </template>
    </CommonLayer>
</template>

<style>
    .redoc-api {
        z-index: 20;
        &__logo {
            transform: translateX(40px);

            display: inline-block;
            align-self: center;

            font-size: 32px;
            font-weight: 600;
            color: #fff;
        }

        .layer__home-link {
            text-decoration: none;
        }

        .layer__logo {
            display: none;
        }

        div[role=search] {
            margin-block-start: 1em;
        }
    }

    .redoc-api-logo-back-button {
        position: relative;
        block-size: 65px;
        margin-inline-start: 1.2em;

        &__button {
            position: absolute;
            inset-inline-start: 0;

            font-size: 32px;
            font-weight: 600;
            color: #fff;
            text-decoration: none;

            transition: 0.4s;

            &:hover {
                opacity: 0.8;
            }
        }
    }

    .kidzonet-back-button-container {
        z-index: 2;

        display: flex;
        justify-content: flex-start;

        padding: 2em;

        background-color: #fff;
        background-color: rgb(250, 250, 250);
    }

    .kidzonet-back-button {
        display: flex;
        align-items: center;
        justify-content: center;

        padding: 0.5em 1em;
        border-radius: 4px;

        font-weight: 700;
        color: white;
        text-decoration: none;

        background-color: rgb(var(--v-theme-primary));

        transition: 0.4s;

        &:hover {
            opacity: 0.8;
        }

        &--dark {
            background-color: rgb(var(--v-theme-surface));
        }
    }
</style>

<style>
    .v-toolbar-title__placeholder {
        display: flex;
        align-items: center;
    }

    .v-toolbar {
        overflow: hidden;
    }
</style>
